// src/hooks/useChannel.js
// Custom hooks for subscribing to Laravel Echo channels.

import { useEffect, useRef, useState, useCallback } from 'react'
import { getEcho } from '@/utils/echo'

/**
 * Subscribe to a private channel and listen for a specific event.
 *
 * @param {string|null} channelName - The private channel name (without 'private-' prefix)
 * @param {string} eventName - The event name to listen for (e.g. 'NotificationCreated')
 * @param {function} callback - Handler called with the event data
 */
export function usePrivateChannel(channelName, eventName, callback) {
  const callbackRef = useRef(callback)
  callbackRef.current = callback

  useEffect(() => {
    const echo = getEcho()
    if (!echo || !channelName) return

    const channel = echo.private(channelName)
    channel.listen(`.${eventName}`, (data) => {
      callbackRef.current(data)
    })

    return () => {
      echo.leave(`private-${channelName}`)
    }
  }, [channelName, eventName])
}

/**
 * Subscribe to a presence channel. Returns online members list and helpers.
 *
 * @param {string|null} channelName - The presence channel name (without 'presence-' prefix)
 * @returns {{ members: object[], here: function, joining: function, leaving: function }}
 */
export function usePresenceChannel(channelName) {
  const [members, setMembers] = useState([])
  const channelRef = useRef(null)

  useEffect(() => {
    const echo = getEcho()
    if (!echo || !channelName) return

    const channel = echo.join(channelName)
    channelRef.current = channel

    channel
      .here((users) => {
        setMembers(users)
      })
      .joining((user) => {
        setMembers((prev) => [...prev, user])
      })
      .leaving((user) => {
        setMembers((prev) => prev.filter((m) => m.id !== user.id))
      })

    return () => {
      echo.leave(`presence-${channelName}`)
      channelRef.current = null
    }
  }, [channelName])

  const listenForEvent = useCallback((eventName, handler) => {
    const channel = channelRef.current
    if (!channel) return () => {}

    channel.listen(`.${eventName}`, handler)

    return () => {
      channel.stopListening(`.${eventName}`)
    }
  }, [])

  return { members, listenForEvent }
}

/**
 * Subscribe to a public channel and listen for a specific event.
 *
 * @param {string|null} channelName - The channel name
 * @param {string} eventName - The event name
 * @param {function} callback - Handler called with the event data
 */
export function useChannel(channelName, eventName, callback) {
  const callbackRef = useRef(callback)
  callbackRef.current = callback

  useEffect(() => {
    const echo = getEcho()
    if (!echo || !channelName) return

    const channel = echo.channel(channelName)
    channel.listen(`.${eventName}`, (data) => {
      callbackRef.current(data)
    })

    return () => {
      echo.leave(channelName)
    }
  }, [channelName, eventName])
}
